import React, { FC, useEffect, useMemo, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import TermsAndConditionsModal from '@web/components/terms-and-conditions-modal';

import { Typography, Button } from '@packages/ui/shared';
import { useAuthState } from '@packages/contexts/auth';
import { getLogger } from '@packages/core/logging';
import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';
import Select from '@web/components/select';
import { useNavigate } from 'react-router-dom';
import { canAccessSTIS } from '@web/utils/can-view-stis';
import { docsService } from '@web/services/singletons';
import { usePublicationsDispatch } from '@web/vin-sessions/components/stis-publications/context/publications.hooks';
import { publicationsActions } from '@web/vin-sessions/components/stis-publications/context/publications.state';

const logger = getLogger('web');

const useAccountSettingsModalStyles = createUseStyles({
    welcome: {
        marginTop: 40,
        marginBottom: 16,
        padding: 16,
    },
    card: {
        backgroundColor: colors.white,
        boxShadow: `0px 1px 4px 0px rgba(0, 0, 0, 0.16)`,
    },
    section: {
        padding: 16,
    },
    footer: {
        borderTop: `1px solid ${colors.grayThree}`,
    },
});

const PilotOnboarding: FC = () => {
    const classes = useAccountSettingsModalStyles();
    const { t } = useTranslation();
    const { account } = useAuthState();
    const navigate = useNavigate();

    const [defaultPhoneId, setDefaultPhoneId] = useState(account?.defaultPhoneNumberId ?? '');
    const [defaultEmailId, setDefaultEmailId] = useState(account?.defaultEmailAddressId ?? '');

    const [isSaving, setIsSaving] = useState(false);
    const [showToS, setShowToS] = useState(false);
    const publicationDispatch = usePublicationsDispatch();
    const stisOptin = useMemo(() => canAccessSTIS(account), [account]);

    const handleOptIn = async () => {
        setIsSaving(true);
        try {
            const result = await docsService.optInUser({});
            if (result?.success) {
                setShowToS(true);
                navigate('/');
                publicationDispatch(publicationsActions.setCanAccessStis({ canAccessStis: true }));
            }
        } catch (error) {
            logger.error('Failed to opt in the user to STIS', { error });
        }
        setIsSaving(false);
    };

    return (
        <div className="h-100 pb-6 overflow-auto">
            <TermsAndConditionsModal
                show={showToS}
                onHide={() => {
                    setShowToS(false);
                }}
            />

            <Row>
                <Col xs={12} md={{ offset: 2, span: 8 }} className={classes.welcome}>
                    <Typography variant="h2">
                        {stisOptin
                            ? t(
                                  'screens:reviewProfile.alreadyOptedIn',
                                  'You have already joined the STIS Search Pilot Program',
                                  { firstName: account?.firstName }
                              )
                            : t(
                                  'screens:reviewProfile.welcome',
                                  'Welcome {{firstName}}. \nJoin the STIS Search Pilot Program',
                                  { firstName: account?.firstName }
                              )}
                    </Typography>
                </Col>

                {/* User Info */}
                <Col xs={12} md={{ offset: 2, span: 8 }} className={classes.card}>
                    <Row className={classes.section}>
                        <Col xs={6} className="mb-4">
                            <div>
                                <Typography variant="label">
                                    {t('views:onboarding.profile.username', 'Subarunet Username')}
                                </Typography>
                            </div>
                            <Typography variant="lead">{account?.soaUsername || 'N/A'}</Typography>
                        </Col>

                        <Col xs={6} className="mb-4">
                            <div>
                                <Typography variant="label">
                                    {t('views:onboarding.profile.fullName', 'Full Name')}
                                </Typography>
                            </div>
                            <Typography variant="lead">{account?.name || 'N/A'}</Typography>
                        </Col>

                        <Col xs={6} className="mb-4">
                            <div>
                                <Typography variant="label">
                                    {t('views:onboarding.profile.phoneNumber', 'Phone')}
                                </Typography>
                            </div>

                            {account && account.phoneNumbers.length > 1 ? (
                                <Select
                                    value={defaultPhoneId}
                                    options={account.phoneNumbers.map((pN) => ({
                                        title: pN.phoneNumberDescription,
                                        value: pN.phoneNumberId,
                                    }))}
                                    onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                        setDefaultPhoneId(event.target.value);
                                    }}
                                />
                            ) : (
                                <Typography variant="lead">
                                    {account?.phoneNumbers[0]?.phoneNumberDescription || 'N/A'}
                                </Typography>
                            )}
                        </Col>

                        <Col xs={6} className="mb-4">
                            <div>
                                <Typography variant="label">{t('views:onboarding.profile.email', 'Email')}</Typography>
                            </div>

                            {account && account.emailAddresses.length > 1 ? (
                                <Select
                                    value={defaultEmailId}
                                    options={account.emailAddresses.map((eA) => ({
                                        title: eA.emailAddress,
                                        value: eA.emailAddressId,
                                    }))}
                                    onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                        setDefaultEmailId(event.target.value);
                                    }}
                                />
                            ) : (
                                <Typography variant="lead">
                                    {account?.emailAddresses[0]?.emailAddress || 'N/A'}
                                </Typography>
                            )}
                        </Col>
                    </Row>
                </Col>

                {/* Terms and Conditions */}
                <Col xs={12} md={{ offset: 2, span: 8 }} className={classes.card}>
                    {!stisOptin && (
                        <Row className={classes.section}>
                            <Col xs={12} className="mb-4">
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="label" style={{ fontSize: 24 }}>
                                        Consent Terms
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        style={{
                                            fontSize: 14,
                                            marginTop: 4,
                                            color: colors.graySix,
                                        }}
                                    >
                                        {t(
                                            'views:onboarding.tos.subtitle',
                                            'Please read the full agreement to proceed.'
                                        )}
                                    </Typography>
                                </div>

                                <div
                                    style={{
                                        padding: '16px',
                                        backgroundColor: colors.grayThree,
                                        borderRadius: '8px',
                                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                        lineHeight: 1.6,
                                        marginBottom: 24,
                                        marginTop: 16,
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Typography
                                        variant="default"
                                        style={{
                                            fontSize: 16,
                                            marginBottom: 16,
                                            color: colors.graySeven,
                                            fontWeight: 600,
                                        }}
                                    >
                                        The STIS pilot is now accepting new users. Please be aware that during this
                                        pilot, the features in the STIS integration in TechShare are under construction
                                        and are being actively developed. This means that new features or changes in
                                        features will occur regularly and some features may have bugs.
                                    </Typography>
                                    <Typography
                                        variant="default"
                                        style={{
                                            fontSize: 16,
                                            marginBottom: 16,
                                            color: colors.graySeven,
                                            fontWeight: 600,
                                        }}
                                    >
                                        By joining the pilot, you agree that you will let the TechShare team know of any
                                        bugs or improvement ideas you have. This includes whether or not a search term
                                        isn’t returning the expected results. Let us know by using the feedback form or
                                        sending us an email at{' '}
                                        <a href="mailto:techshare_support@subaru.com">techshare_support@subaru.com</a>.
                                    </Typography>
                                    <Typography
                                        variant="default"
                                        style={{
                                            fontSize: 16,
                                            marginBottom: 16,
                                            color: colors.graySeven,
                                            fontWeight: 600,
                                        }}
                                    >
                                        Additionally, the current STIS application is still available and updated as
                                        normal. Please use Subarunet’s STIS at anytime when TechShare’s STIS
                                        functionality isn’t working as expected or a feature is not yet available. You
                                        can access it here:{' '}
                                        <a
                                            href="https://www.subarunet.com/stis"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            https://www.subarunet.com/stis
                                        </a>
                                    </Typography>
                                </div>
                                <Typography
                                    variant="default"
                                    style={{
                                        marginTop: 16,
                                        color: colors.graySix,
                                    }}
                                >
                                    If you choose to decline, no changes will be made to your current access.
                                </Typography>
                            </Col>
                        </Row>
                    )}

                    <Row className={classNames(classes.section, classes.footer)}>
                        <Col xs={12}>
                            <div className="d-flex align-items-end justify-content-end">
                                <div className="d-flex">
                                    {stisOptin ? (
                                        <Button
                                            title="Continue to Dashboard"
                                            onPress={() => {
                                                navigate('/');
                                            }}
                                        />
                                    ) : (
                                        <>
                                            <Button
                                                title="Decline Invitation"
                                                onPress={() => {
                                                    navigate('/');
                                                }}
                                                style={{ marginRight: 8 }}
                                                variant="ghost-blue"
                                            />
                                            <Button
                                                title="Accept and Enroll"
                                                isLoading={isSaving}
                                                onPress={() => {
                                                    if (!account) {
                                                        return;
                                                    }
                                                    handleOptIn();
                                                }}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Col>

                <Col xs={12} md={{ offset: 2, span: 8 }}>
                    <Row>
                        <Col xs={12} md={{ offset: 3, span: 6 }} className="mt-4 text-center">
                            <Typography variant="caption">
                                {account?.isRetailerUser
                                    ? t(
                                          'views:onboarding.retailerContactInfo',
                                          'If anything looks incorrect, contact your DEM admin (Dealer Employee Maintenance Administrator).'
                                      )
                                    : t(
                                          'views:onboarding.contactInfo',
                                          'If anything looks incorrect, contact the Subaru Helpdesk\r\nat 1-800-SOA-STAR (1-800-762-7827).'
                                      )}
                            </Typography>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </div>
    );
};

export default PilotOnboarding;
